import AppCard from '@nxsys/components/AppCard';
import { PAYMENT_MODEL_ENUM } from '@nxsys/constants/AppEnums';
import { defaultTheme } from '@nxsys/constants/defaultConfig';
import { useGetEmployeeById } from '@nxsys/hooks/api/employee/useEmployeeApi';
import { useGetIrregularHolidayWorkerSummary } from '@nxsys/hooks/api';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Icons } from '@nxsys/types/icons';
import { showCurrencyByCountry } from '@nxsys/utils';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Skeleton } from 'antd';
import { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

const HolidayPayBalance = () => {
  const { messages } = useTypedIntl();
  const { payrollCountry, user } = useSelector(
    (state: AppState) => state.appStore,
  );
  const employeeId = (user?.subcontractorId as any)?.id;

  const {
    run: getEmployeeById,
    value: employeeValue,
    loading,
  } = useGetEmployeeById();

  useEffect(() => {
    if (employeeId) {
      getEmployeeById(employeeId);
    }
  }, [employeeId]);

  const settings = employeeValue?.data?.setting;
  const averageData = employeeValue?.data?.average52WeekData;
  const paymentModel = employeeValue?.data?.paymentModel;
  const holidayPayMethod = settings?.holidayPayMethod;

  // Hours fields only display for PAYEE payment model with FIFTY_TWO_WEEK_AVERAGE method
  const isPayeeModel = paymentModel === PAYMENT_MODEL_ENUM.PAYEE;
  const isFiftyTwoWeekAverage = holidayPayMethod === 'FIFTY_TWO_WEEK_AVERAGE';
  const shouldShowHours = isPayeeModel && isFiftyTwoWeekAverage;

  // NX-6284: Ireland 8% of worked hours (irregular). Gated on `applies` from
  // the summary call, not this worker's own `holidayPayMethod` — a PAYE
  // worker who inherits the method from the company (the normal migration
  // path, no override on their own setting) would otherwise be missed.
  const isIrelandTenant = payrollCountry === IRELAND;
  const { run: getIrregularHolidaySummary, value: irregularHolidaySummary } =
    useGetIrregularHolidayWorkerSummary();

  useEffect(() => {
    if (employeeId && isIrelandTenant) {
      getIrregularHolidaySummary(employeeId);
    }
  }, [employeeId, isIrelandTenant]);

  const isIrregular = !!irregularHolidaySummary?.applies;

  // Money values - always displayed
  const holidayPayData = useMemo(() => {
    return {
      accrued: settings?.holidayPaySumOfAccrued ?? 0,
      paid: settings?.holidayPaySumOfPaid ?? 0,
      remaining: settings?.holidayPayBalance ?? 0,
    };
  }, [settings]);

  // Hours values - only for PAYEE with FIFTY_TWO_WEEK_AVERAGE method
  const holidayHoursData = useMemo(() => {
    if (!shouldShowHours) {
      return null;
    }

    const holidayPayRateValue =
      Number(settings?.holidayPayRate) / 100 || 0.1207;

    // Accrued Holiday Hours calculation
    const accruedHours =
      Number(averageData?.totalAccrualHours || 0) ||
      Number(
        Number(averageData?.averageHours || 0) *
          Number(averageData?.totalWeeksUsed || 0) *
          holidayPayRateValue,
      ) ||
      0;

    // Paid Holiday Hours
    const paidHours = Number(averageData?.paidHolidayHours || 0);

    // Remaining Holiday Hours calculation
    const holidayRate = Number(settings?.holidayPayRate) / 100 || 0.1207;
    const remainingHours = Math.max(
      0,
      Number(
        Number(averageData?.averageHours || 0) *
          Number(averageData?.totalWeeksUsed || 0) *
          holidayRate -
          paidHours,
      ),
    );

    return {
      accruedHours,
      paidHours,
      remainingHours,
    };
  }, [shouldShowHours, averageData, settings]);

  // Money data array
  const moneyData = useMemo(
    () => [
      {
        label: 'Holiday Pay Accrued',
        value: formatMoney(
          holidayPayData.accrued,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: (
          <Icons.IconCalendar
            width={20}
            height={20}
            color={defaultTheme.theme.palette.violet[500]}
          />
        ),
      },
      {
        label: 'Holiday Pay Paid',
        value: formatMoney(
          holidayPayData.paid,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: (
          <Icons.IconCalendarPlus2
            width={20}
            height={20}
            color={defaultTheme.theme.palette.violet[500]}
          />
        ),
      },
      {
        label: 'Remaining',
        value: formatMoney(
          holidayPayData.remaining,
          showCurrencyByCountry(payrollCountry),
        ),
        icon: (
          <Icons.IconCalendarClock
            width={20}
            height={20}
            color={defaultTheme.theme.palette.violet[500]}
          />
        ),
      },
    ],
    [holidayPayData, payrollCountry],
  );

  // Hours data array (only for FIFTY_TWO_WEEK_AVERAGE)
  const hoursData = useMemo(() => {
    if (!holidayHoursData) return [];

    return [
      {
        label: 'Accrued Holiday Hours',
        value: `${holidayHoursData.accruedHours.toFixed(2)}`,
        icon: (
          <Icons.IconClock
            width={20}
            height={20}
            color={defaultTheme.theme.palette.violet[500]}
          />
        ),
      },
      {
        label: 'Paid Holiday (PRHP)',
        value: `${holidayHoursData.paidHours.toFixed(2)}`,
        icon: (
          <Icons.IconCalendarPlus
            width={20}
            height={20}
            color={defaultTheme.theme.palette.violet[500]}
          />
        ),
      },
      {
        label: 'Remaining Holiday',
        value: `${holidayHoursData.remainingHours.toFixed(2)}`,
        icon: (
          <Icons.IconCalendarEmpty
            width={20}
            height={20}
            color={defaultTheme.theme.palette.violet[500]}
          />
        ),
      },
    ];
  }, [holidayHoursData]);

  // NX-6284: Holiday Hours Accrued / Paid / Remaining + Holiday Pay Rate,
  // using nxsys wording. PAYE only — the method never applies to umbrella.
  // Ireland 8% irregular method only — do not reuse the shared UK 52-week
  // keys (common.accruedHolidayHours etc.) here.
  const irregularRows = useMemo(() => {
    if (!isIrregular || !irregularHolidaySummary) return [];
    return [
      {
        label: messages['common.holidayHoursAccrued'] ?? 'Holiday Hours Accrued',
        value: `${Number(irregularHolidaySummary.hours?.accrued ?? 0).toFixed(2)}`,
      },
      {
        label: messages['common.holidayHoursPaid'] ?? 'Holiday Hours Paid',
        value: `${Number(irregularHolidaySummary.hours?.paid ?? 0).toFixed(2)}`,
      },
      {
        label: messages['common.holidayHoursRemaining'] ?? 'Holiday Hours Remaining',
        value: `${Number(irregularHolidaySummary.remainingHours ?? 0).toFixed(2)} (≈ ${formatMoney(irregularHolidaySummary.remainingValueEstimate ?? 0, showCurrencyByCountry(payrollCountry))})`,
      },
      {
        label: 'Holiday Pay Rate',
        value: `${formatMoney(irregularHolidaySummary.holidayPayRate ?? 0, showCurrencyByCountry(payrollCountry))}/h`,
      },
    ];
  }, [isIrregular, irregularHolidaySummary, payrollCountry, messages]);

  if (loading && !employeeValue) {
    return (
      <AppCard className="h-full">
        <div className="p-[20px]">
          <Skeleton active />
        </div>
      </AppCard>
    );
  }

  return (
    <AppCard className="h-full">
      <div className="p-[20px]">
        <div className="text-[16px] font-[500] mb-[6px]">
          Holiday Pay Balance
        </div>
        <div className="text-[12px] font-[400] text-neutral-500 mb-[16px]">
          Track your entitlement in both days and value.
        </div>
        <div className="space-y-[16px]">
          {/* NX-6284: Ireland 8% irregular — the legacy € ledger below is
              always 0 for this method, so it is replaced entirely. */}
          {isIrregular ? (
            irregularRows.map((item, index) => (
              <div
                key={`irregular-${index}`}
                className="flex items-center gap-x-[12px] border border-[#CDDDD9] p-[12px] rounded-[8px]"
              >
                <Icons.IconCalendarClock
                  width={20}
                  height={20}
                  color={defaultTheme.theme.palette.violet[500]}
                />
                <div className="text-[14px] font-[400] text-neutral-500 flex-1">
                  {item.label}
                </div>
                <div className="text-[14px] font-[500] text-neutral-700">
                  {item.value}
                </div>
              </div>
            ))
          ) : (
            <>
              {/* Money values - always displayed */}
              {moneyData.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-x-[12px] border border-[#CDDDD9] p-[12px] rounded-[8px]"
                >
                  {item.icon}
                  <div className="text-[14px] font-[400] text-neutral-500 flex-1">
                    {item.label}
                  </div>
                  <div className="text-[14px] font-[500] text-neutral-700">
                    {item.value}
                  </div>
                </div>
              ))}

              {/* Hours values - only for PAYEE with FIFTY_TWO_WEEK_AVERAGE */}
              {shouldShowHours &&
                hoursData.map((item, index) => (
                  <div
                    key={`hours-${index}`}
                    className="flex items-center gap-x-[12px] border border-[#CDDDD9] p-[12px] rounded-[8px]"
                  >
                    {item.icon}
                    <div className="text-[14px] font-[400] text-neutral-500 flex-1">
                      {item.label}
                    </div>
                    <div className="text-[14px] font-[500] text-neutral-700">
                      {item.value}
                    </div>
                  </div>
                ))}
            </>
          )}
        </div>
      </div>
    </AppCard>
  );
};

export default HolidayPayBalance;
